import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#watchForStuckPointerEvents();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#pointerEventsRecoveryTimer);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pointerEventsRecoveryTimer;

  /**
   * Third-party apps (e.g. combined-listing color switchers) can set `pointer-events: none`
   * inline on this element as part of their own fade transition when swapping media, and
   * occasionally fail to clear it afterwards, leaving the gallery unswipeable until reload.
   * This watches for that specific stuck state and clears it if it's still present after a
   * grace period well beyond any normal transition, without touching anything else the app does.
   */
  #watchForStuckPointerEvents() {
    const { signal } = this.#controller;
    const observer = new MutationObserver(() => {
      if (this.style.pointerEvents !== 'none') return;

      clearTimeout(this.#pointerEventsRecoveryTimer);
      this.#pointerEventsRecoveryTimer = setTimeout(() => {
        if (this.style.pointerEvents === 'none') this.style.removeProperty('pointer-events');
      }, 2000);
    });

    observer.observe(this, { attributes: true, attributeFilter: ['style'] });
    signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Waits for the new gallery's first image to finish downloading and decoding first, so the
   * browser keeps showing the current photo until the replacement is actually ready to paint,
   * instead of swapping to a blank gallery that then pops the image in once it loads.
   *
   * The wait is capped: `Image.decode()` is normally fast (usually well under a second, even
   * for a large uncached photo), but for a small number of images it can hang indefinitely
   * and never resolve or reject at all — observed directly against a real photo on this store.
   * Without a cap, that leaves the gallery frozen on the old photo forever. The cap only ever
   * matters for that rare, otherwise-unrecoverable case; normal decodes finish long before it.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    /* Every variant update replaces the gallery, including size changes, where
       the photos and their order are identical. That swap is what makes the
       page flash and jump, so skip it when there is nothing to change: compare
       the images in order, which also catches the case where the same photos
       come back re-sorted around a new featured image. */
    const newFingerprint = galleryFingerprint(newMediaGallery);
    if (newFingerprint && newFingerprint === galleryFingerprint(this)) return;

    const newImage = newMediaGallery.querySelector('img');

    if (newImage) {
      const preload = new Image();
      if (newImage.srcset) preload.srcset = newImage.srcset;
      if (newImage.sizes) preload.sizes = newImage.sizes;
      preload.src = newImage.currentSrc || newImage.src;

      await Promise.race([preload.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 3000))]);

      /* The bytes are decoded above, but Safari still decodes asynchronously
         when the element is inserted, leaving the gallery white for a frame or
         two on every colour change. Asking for a synchronous decode makes it
         paint the photo in the same frame as the swap. */
      newImage.decoding = 'sync';
      newImage.loading = 'eager';
      newImage.fetchPriority = 'high';
    }

    /* Hold the height the gallery has right now across the swap. The incoming
       gallery measures zero until its slideshow has laid out, and everything
       below it jumps up the page for that one frame before snapping back.
       Released once the new gallery has had two frames to settle, so a colour
       whose photos are a different shape still ends up at its own height. */
    const { height } = this.getBoundingClientRect();
    if (height) newMediaGallery.style.minHeight = `${height}px`;

    /* Pin the column the gallery sits in as well. On a phone its height comes
       entirely from the gallery, and unlike the gallery itself this element is
       never detached, so it holds the space even in the instant the old
       gallery is out and the new one has not been laid out yet. */
    const column = this.parentElement;
    const columnHeight = column?.getBoundingClientRect().height ?? 0;
    if (column && columnHeight) column.style.minHeight = `${columnHeight}px`;

    this.replaceWith(newMediaGallery);

    const release = () => {
      newMediaGallery.style.removeProperty('min-height');
      column?.style.removeProperty('min-height');
    };

    /* Hold the reservation until the replacement has actually laid out, rather
       than for a fixed couple of frames. Safari leaves the new slideshow at
       zero height for a while after it is inserted - long enough that letting
       go on a timer just moved the collapse later. The scroller is measured
       because it carries no reservation of its own, so it reports the real
       height; the gallery itself would only report the reserved one.

       The frame budget is the ceiling for a replacement that never reports a
       height at all, and the timeout is the backstop for a hidden tab, where
       animation frames stop firing altogether. Releasing more than once is
       harmless. */
    const scroller = newMediaGallery.querySelector('slideshow-slides');

    const releaseWhenLaidOut = (framesLeft) => {
      if (!scroller || scroller.getBoundingClientRect().height > 0 || framesLeft <= 0) {
        release();
        return;
      }

      requestAnimationFrame(() => releaseWhenLaidOut(framesLeft - 1));
    };

    requestAnimationFrame(() => releaseWhenLaidOut(60));
    setTimeout(release, 2000);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/**
 * Identifies the photos a gallery is showing, in order, so two galleries can be
 * compared. Order matters: the theme sorts the selected variant's image to the
 * front, so a re-sort is a real change even when the set of photos is the same.
 *
 * @param {Element} gallery - The gallery to describe.
 * @returns {string} A comparable description of the gallery's media.
 */
function galleryFingerprint(gallery) {
  const sources = Array.from(gallery.querySelectorAll('slideshow-slide'), (slide) => {
    const image = slide.querySelector('img');
    return image?.getAttribute('src') || image?.getAttribute('srcset') || '';
  });

  /* An empty description means there was nothing to compare - a gallery of
     videos or models, say. Return nothing so the caller swaps as before rather
     than treating two unknowns as a match. */
  return sources.some(Boolean) ? sources.join('|') : '';
}
